/* eslint-disable jsx-a11y/label-has-associated-control */
// Implicit association, see this: https://www.w3.org/WAI/tutorials/forms/labels/#associating-labels-implicitly
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { addDataLayer } from 'lib/GoogleTagManager';

const uiProps = {
  size: 22,
  borderThickness: 2,
  selectedBorderThickness: 11,
};

const Wrapper = styled.label`

  display: flex;
  cursor: pointer;
  &:hover,
  &:focus-within{
    background-color: rgba(0,0,0,0.02);
  }

  .checkIcon {
    position: absolute;
    left:calc(${uiProps.size / 2}px - 0.5em);
    top:calc(${uiProps.size / 2}px - 0.5em);
    color: rgba(0,0,0,0.08);
  }


  &.disabled {

    cursor: default;
    &:hover,
    &:focus-within{
      background-color: transparent;
    }

    color: ${({ theme }) => theme.palette.grey[2]};
    .checkboxUi {
      border-color: ${({ theme }) => theme.palette.grey[3]}!important;
    }
  }

  &.readonly {
    opacity: .5;
  }
`;

const ChildrenWrapper = styled.div`
  flex-grow:1;
`;

const CheckboxUi = styled.div`
  width: calc(${uiProps.size}px - ${uiProps.borderThickness * 2}px);
  height: calc(${uiProps.size}px - ${uiProps.borderThickness * 2}px);

  box-sizing: content-box;
  transition: all .2s;
`;

const CheckboxUiWrapper = styled.div`
  flex-shrink:0;
  position: relative;
  input {
    position: absolute;
    opacity:0;
    width:0;
  }
  ${CheckboxUi} {
    border: ${uiProps.borderThickness}px solid ${({ theme }) => theme.palette.grey[1]};
    border-radius: 3px;
  }


  input:checked + ${CheckboxUi} {
    width: calc(${uiProps.size}px - ${uiProps.selectedBorderThickness * 2}px);
    height: calc(${uiProps.size}px - ${uiProps.selectedBorderThickness * 2}px);
    border-width: ${uiProps.selectedBorderThickness}px;
    border-color: ${({ theme }) => theme.palette.primary.main};

  }
  input:checked + ${CheckboxUi} + .checkIcon {
    color: ${({ theme }) => theme.palette.colors.white};
  }
`;

const Checkbox = ({
  className, value, children, name, checked, defaultChecked, readOnly, disabled, onChange,
  dataLayer, ...rest
}) => {
  const classes = ['Checkbox'];
  if (disabled) classes.push('disabled');
  if (readOnly) classes.push('readonly');
  if (className) classes.push(className);

  const handleChange = (e) => {
    if (dataLayer) {
      addDataLayer({ ...dataLayer, label: e.target.checked ? 'Check' : 'Uncheck' });
    }
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <Wrapper {...{ rest }} className={classes.join(' ')}>
      <CheckboxUiWrapper className="checkboxUiWrapper">
        <input
          type="checkbox"
          name={name}
          value={value}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled || readOnly}
          onChange={handleChange}
          readOnly={readOnly}
        />
        <CheckboxUi className="checkboxUi" />
        <FontAwesomeIcon className="checkIcon" icon={faCheck} />
      </CheckboxUiWrapper>
      {children && (
        <ChildrenWrapper className="checkboxChildrenWrapper">
          {children}
        </ChildrenWrapper>
      )}
    </Wrapper>
  );
};

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  dataLayer: PropTypes.shape({}),
};

Checkbox.defaultProps = {
  className: null,
  checked: undefined,
  disabled: false,
  readOnly: false,
  value: undefined,
  name: undefined,
  defaultChecked: undefined,
  dataLayer: null,
};

export default Checkbox;

export const CheckboxesArea = styled.div`
> * {
  .checkboxUiWrapper {
    margin-right: ${({ theme }) => theme.spacing}px;
  }
  .checkboxChildrenWrapper {
    h4 {
      color: ${({ theme }) => theme.palette.text.main};
    }
    p{
      margin-bottom: 0;
    }
  }
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing * 6}px;
  }
}
`;
